import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { payoutStatusLabels } from '@entities/wallet/model/payout-display'
import { analyticsDateRange, ANALYTICS_TIME_ZONE } from '@shared/lib/analytics-period'
import { useState } from 'react'
import { WalletCards } from 'lucide-react'
import { resolveDateRange, DEFAULT_DATE_RANGE } from '@shared/lib/date-range'
import type { DateRangeValue } from '@shared/lib/date-range'
import {
  Banner,
  Button,
  Card,
  DateRangeField,
  SearchField,
  SelectField,
  Drawer,
  DrawerSection,
  EmptyState,
  ErrorState,
  KeyValueGrid,
  LoadMore,
  MoneyValue,
  NavigationLink,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import type { MonetaryValue } from '@shared/lib/money/money'
import { getPayoutFailureFeedback } from '@shared/lib/errors/user-facing-error'
import {
  useRefreshReceipts,
  useConnectComposition,
  useConnectPayout,
  useConnectPayouts,
} from '@entities/wallet/api/connect.queries'
import type {
  ConnectPayout,
  ConnectMovement,
  ConnectSync,
} from '@entities/wallet/api/connect.contract'
import {
  page,
  headerText,
  toolbarControls,
  sectionTitle,
  statementItem,
  statementItemHeader,
  statementItemSubtitle,
  helpText,
  noticeAction,
} from './wallet.css'

const syncLabels = {
  pending: 'Aguardando sincronização',
  current: 'Consulta atualizada',
  stale: 'Consulta desatualizada',
  unavailable: 'Consulta indisponível',
} as const
function legacyDate(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC',
      }).format(new Date(value)) + ' UTC'
    : 'Não informado'
}
function useReceiptDate() {
  const enabled = useFeatureFlag('receiptsExperience')
  return (value: string | null) =>
    enabled && value
      ? new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZone: ANALYTICS_TIME_ZONE,
        }).format(new Date(value)) + ' (Brasília)'
      : legacyDate(value)
}
function arrivalDate(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'UTC' }).format(
        new Date(value),
      )
    : 'Previsão indisponível'
}
const movementLabels: Readonly<Record<string, string>> = {
  payment: 'Pagamento',
  charge: 'Cobrança',
  refund: 'Reembolso',
  payment_refund: 'Reembolso de pagamento',
  transfer: 'Transferência entre saldos',
  transfer_refund: 'Reversão de transferência',
  payout: 'Envio ao banco',
  payout_failure: 'Devolução de envio bancário',
  payout_cancel: 'Cancelamento de envio bancário',
  adjustment: 'Ajuste',
  fee: 'Tarifa',
  stripe_fee: 'Tarifa Stripe',
  reserve_transaction: 'Movimentação de reserva',
  reserved_funds: 'Fundos reservados',
}
function movementLabel(value: string | null, fallback: string, receiptsExperience: boolean) {
  if (!value || !/^[a-z][a-z0-9_]{0,79}$/.test(value)) return fallback
  const label = Object.hasOwn(movementLabels, value) ? movementLabels[value]! : fallback
  if (receiptsExperience) return value === 'stripe_fee' ? 'Tarifa de processamento' : label
  return `${label} (${value})`
}
function Amount({ value }: { value: MonetaryValue | null }) {
  return value ? <MoneyValue {...value} /> : <span>Não disponível</span>
}
function SyncNotice({ sync }: { sync?: ConnectSync }) {
  const date = useReceiptDate()
  if (!sync) return null
  return (
    <p className={helpText}>
      {syncLabels[sync.syncStatus]} · Última sincronização: {date(sync.lastSyncedAt)}
    </p>
  )
}
function Movements({
  items,
  onViewSale,
}: {
  items: ConnectMovement[]
  onViewSale?: (id: string) => void
}) {
  const date = useReceiptDate()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  return (
    <div>
      {items.map((item) => (
        <div key={item.id} className={statementItem}>
          <div className={statementItemHeader}>
            <strong className={statementItemSubtitle}>{item.id}</strong>
            <span>{date(item.createdAt)}</span>
          </div>
          <KeyValueGrid
            items={[
              {
                label: 'Natureza',
                value: movementLabel(
                  item.type,
                  receiptsExperience ? 'Outro movimento' : 'Outro movimento Stripe',
                  receiptsExperience,
                ),
              },
              {
                label: 'Categoria',
                value: movementLabel(
                  item.reportingCategory,
                  item.reportingCategory
                    ? receiptsExperience
                      ? 'Outra categoria'
                      : 'Outra categoria Stripe'
                    : 'Categoria indisponível',
                  receiptsExperience,
                ),
              },
              { label: 'Valor', value: <Amount value={item.amount} /> },
              { label: 'Taxa', value: <Amount value={item.fee} /> },
              { label: 'Líquido', value: <Amount value={item.net} /> },
              { label: 'Disponibilidade informada', value: date(item.availableOn) },
              { label: 'Referência de origem', value: item.sourceId ?? 'Não informada' },
              { label: 'Repasse', value: item.payoutId ?? 'Não informado' },
              {
                label: 'Status do movimento',
                value: item.status === 'available' ? 'Disponível' : 'Pendente',
              },
            ]}
          />
          {item.saleIds.length === 0 ? (
            <p className={helpText}>Origem comercial indisponível</p>
          ) : (
            item.saleIds.map((id) =>
              onViewSale ? (
                <NavigationLink key={id} onClick={() => onViewSale(id)}>
                  Ver venda {id}
                </NavigationLink>
              ) : (
                <p key={id}>Venda vinculada: {id}</p>
              ),
            )
          )}
        </div>
      ))}
    </div>
  )
}
function Bank({ payout }: { payout: ConnectPayout }) {
  return (
    <span>
      {payout.destination?.bankName ?? 'Banco não informado'}
      {payout.destination?.last4 ? ` · Final ${payout.destination.last4}` : ''}
    </span>
  )
}
const compositionLabels = {
  pending: 'Composição pendente',
  complete: 'Composição completa',
  unavailable: 'Composição indisponível',
  divergent: 'Composição em conferência',
} as const
const ledgerLabels = {
  pending: 'Conciliação na carteira pendente',
  completed: 'Repasse conciliado na carteira',
  reversed: 'Repasse revertido na carteira',
} as const
const ledgerListLabels = {
  pending: 'Conciliação pendente',
  completed: 'Conciliado na carteira',
  reversed: 'Revertido na carteira',
} as const
const ledgerPendingLabels: Readonly<Record<string, string>> = {
  composition_pending: 'A composição do repasse ainda não está completa.',
  composition_divergent: 'A composição do repasse precisa de conferência.',
  allocation_or_eligibility_unproven:
    'As referências e a elegibilidade dos recebíveis ou reservas ainda não foram comprovadas.',
} as const
function LedgerNotice({ payout }: { payout: ConnectPayout }) {
  const enabled = useFeatureFlag('receiptsExperience')
  const explanation =
    payout.ledgerStatus === 'pending'
      ? `${payout.ledgerPendingReason === 'composition_divergent' ? 'Os valores informados para este saque precisam de conferência.' : payout.ledgerPendingReason === 'composition_pending' ? 'Estamos aguardando os detalhes dos valores enviados.' : 'Estamos conferindo as vendas e reservas incluídas neste saque.'} A atualização da carteira ainda não foi concluída.`
      : payout.ledgerStatus === 'completed'
        ? 'Os valores deste saque já foram registrados na carteira.'
        : 'O registro anterior deste saque foi revertido na carteira.'
  return (
    <Banner
      tone={payout.ledgerStatus === 'pending' ? 'warning' : 'info'}
      title={
        enabled
          ? {
              pending: 'Conferência do saque em andamento',
              completed: 'Conferência do saque concluída',
              reversed: 'Registro do saque revertido',
            }[payout.ledgerStatus]
          : ledgerLabels[payout.ledgerStatus]
      }
    >
      {enabled
        ? explanation
        : payout.ledgerStatus === 'pending'
          ? `${payout.ledgerPendingReason && Object.hasOwn(ledgerPendingLabels, payout.ledgerPendingReason) ? ledgerPendingLabels[payout.ledgerPendingReason] : 'A conciliação ainda está em análise.'} Este repasse ainda não produziu efeito financeiro na carteira, mesmo quando a Stripe informa pago.`
          : payout.ledgerStatus === 'completed'
            ? 'O efeito financeiro deste repasse foi aplicado na carteira.'
            : 'O efeito financeiro anteriormente aplicado foi revertido na carteira.'}{' '}
      {enabled
        ? 'O status do saque e a conferência da carteira não confirmam crédito bancário.'
        : 'O status da Stripe e a conciliação na carteira não confirmam crédito bancário.'}
    </Banner>
  )
}
function PayoutDetail({
  id,
  onClose,
  onViewSale,
}: {
  id: string | null
  onClose: () => void
  onViewSale?: (id: string) => void
}) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const statusLabels = payoutStatusLabels(receiptsExperience)
  const refresh = useRefreshReceipts()
  const date = useReceiptDate()
  const [compositionOpen, setCompositionOpen] = useState(false)
  const showComposition = !receiptsExperience || compositionOpen
  const detail = useConnectPayout(id)
  const composition = useConnectComposition(id, showComposition)
  const payout = detail.data
  const failureFeedback = getPayoutFailureFeedback(payout?.failure?.code ?? null)
  const items = composition.data?.pages.flatMap((page) => page.items) ?? []
  return (
    <Drawer
      data-testid="payout-detail"
      open={id !== null}
      onClose={onClose}
      title={receiptsExperience ? 'Detalhes do saque automático' : 'Detalhes do repasse'}
      variant="financial"
    >
      <SyncNotice sync={detail.data} />
      {receiptsExperience ? (
        <Button
          data-testid="payout-refresh-btn"
          variant="secondary"
          disabled={detail.isFetching || composition.isFetching}
          onClick={() => {
            void refresh()
          }}
        >
          Atualizar consulta
        </Button>
      ) : null}
      {detail.isPending && !payout ? <Skeleton height={120} /> : null}
      {detail.isError ? (
        <ErrorState
          title="Não foi possível consultar o repasse"
          description="Tente carregar novamente."
          onRetry={() => {
            void detail.refetch()
          }}
        />
      ) : null}
      {payout ? (
        <DrawerSection title={payout.id}>
          <LedgerNotice payout={payout} />
          <KeyValueGrid
            items={[
              { label: 'Status', value: statusLabels[payout.status] },
              { label: 'Valor', value: <Amount value={payout.amount} /> },
              { label: 'Criado em', value: date(payout.createdAt) },
              {
                label: receiptsExperience
                  ? 'Data estimada de chegada'
                  : 'Data estimada de chegada (UTC)',
                value: arrivalDate(payout.arrivalDate),
              },
              { label: 'Destino registrado neste repasse', value: <Bank payout={payout} /> },
            ]}
          />
          {payout.status === 'failed' || payout.failure ? (
            <Banner tone="danger" title={failureFeedback.title}>
              {failureFeedback.description}
              <span className={noticeAction}>
                <NavigationLink href="/onboarding#conta-bancaria">
                  Conferir cadastro bancário
                </NavigationLink>
              </span>
            </Banner>
          ) : null}
          <p className={helpText}>
            O status e a data estimada refletem a consulta. Eles não garantem crédito na conta
            bancária.
          </p>
        </DrawerSection>
      ) : !detail.isPending && !detail.isError ? (
        <EmptyState
          icon={<WalletCards size={22} />}
          title="Repasse não disponível"
          description="Atualize a consulta para tentar novamente."
        />
      ) : null}
      {receiptsExperience ? (
        <Button
          data-testid="payout-composition-toggle"
          variant="secondary"
          aria-expanded={compositionOpen}
          onClick={() => setCompositionOpen((open) => !open)}
        >
          {compositionOpen ? 'Ocultar composição' : 'Ver composição'}
        </Button>
      ) : null}
      {showComposition ? (
        <DrawerSection title="Composição do repasse">
          <SyncNotice sync={composition.data?.pages[0]} />
          {composition.data?.pages[0] ? (
            <p>{compositionLabels[composition.data.pages[0].compositionStatus]}</p>
          ) : payout ? (
            <p>{compositionLabels[payout.compositionStatus]}</p>
          ) : null}
          {composition.isPending ? <Skeleton height={100} /> : null}
          {composition.isError ? (
            <ErrorState
              title="Não foi possível consultar a composição"
              description="Os dados já carregados continuam visíveis. Tente novamente."
              onRetry={() => {
                void composition.refetch()
              }}
            />
          ) : null}
          <Movements items={items} onViewSale={onViewSale} />
          {!composition.isPending && !composition.isError && items.length === 0 ? (
            <EmptyState
              icon={<WalletCards size={22} />}
              title={
                composition.data?.pages[0]?.compositionStatus === 'complete'
                  ? 'Nenhum movimento na composição'
                  : 'Composição ainda não disponível'
              }
              description="Aguarde a sincronização dos movimentos deste repasse."
            />
          ) : null}
          <LoadMore
            shown={items.length}
            hasMore={Boolean(composition.hasNextPage)}
            loading={composition.isFetchingNextPage}
            onClick={() => {
              void composition.fetchNextPage()
            }}
          />
        </DrawerSection>
      ) : null}
    </Drawer>
  )
}

export function AutomaticPayouts({ onViewSale }: { onViewSale?: (id: string) => void }) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const statusLabels = payoutStatusLabels(receiptsExperience)
  const refresh = useRefreshReceipts()
  const date = useReceiptDate()
  const [range, setRange] = useState<DateRangeValue>(DEFAULT_DATE_RANGE)
  const [status, setStatus] = useState<ConnectPayout['status'] | ''>('')
  const [query, setQuery] = useState('')
  const dates = receiptsExperience ? analyticsDateRange(range) : resolveDateRange(range)
  const payouts = useConnectPayouts({
    currency: 'BRL',
    dateFrom: dates.dateFrom ?? undefined,
    dateTo: dates.dateTo ? new Date(Date.parse(dates.dateTo) - 1).toISOString() : undefined,
    status: status || undefined,
    q: query || undefined,
  })
  const [selectedPayout, setSelectedPayout] = useState<string | null>(null)
  const transfers = payouts.data?.pages.flatMap((page) => page.items) ?? []
  return (
    <div className={page} data-testid="automatic-payouts-screen">
      <div className={headerText}>
        <p className={helpText}>
          {receiptsExperience
            ? 'Período dos saques automáticos (horário de Brasília)'
            : 'Período dos repasses automáticos (UTC)'}
        </p>
        <DateRangeField value={range} onChange={setRange} />
      </div>
      <Card padding="md" className={toolbarControls}>
        <h2 className={sectionTitle}>{receiptsExperience ? 'Saques automáticos' : 'Repasses'}</h2>
        {receiptsExperience ? (
          <Button
            data-testid="payouts-refresh-btn"
            variant="secondary"
            disabled={payouts.isFetching}
            onClick={() => void refresh()}
          >
            Atualizar
          </Button>
        ) : null}
        <SearchField
          value={query}
          onChange={setQuery}
          ariaLabel="Buscar repasses"
          placeholder="Buscar repasse"
        />
        <SelectField
          label="Status do repasse"
          value={status}
          options={[
            { value: '', label: 'Todos' },
            ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          ]}
          onChange={(event) => setStatus(event.target.value as ConnectPayout['status'] | '')}
        />
        <SyncNotice sync={payouts.data?.pages[0]} />
        {payouts.isPending ? <Skeleton height={100} /> : null}
        {payouts.isError ? (
          <ErrorState
            title="Não foi possível consultar os repasses"
            description="Tente novamente. Os repasses já carregados continuam visíveis."
            onRetry={() => {
              void payouts.refetch()
            }}
          />
        ) : null}
        {transfers.map((payout) => (
          <div className={statementItem} key={payout.id}>
            {payout.syncStatus !== 'current' ? <SyncNotice sync={payout} /> : null}
            <div className={statementItemHeader}>
              <strong className={statementItemSubtitle}>{payout.id}</strong>
              <StatusBadge
                label={statusLabels[payout.status]}
                tone={
                  payout.status === 'failed'
                    ? 'closed'
                    : payout.status === 'paid'
                      ? 'success'
                      : 'neutral'
                }
              />
            </div>
            <StatusBadge
              label={
                receiptsExperience
                  ? {
                      pending: 'Em conferência',
                      completed: 'Conferência concluída',
                      reversed: 'Registro revertido',
                    }[payout.ledgerStatus]
                  : ledgerListLabels[payout.ledgerStatus]
              }
              tone={
                payout.ledgerStatus === 'completed'
                  ? 'success'
                  : payout.ledgerStatus === 'reversed'
                    ? 'closed'
                    : 'informative'
              }
            />
            <Amount value={payout.amount} />
            <Bank payout={payout} />
            <span>{date(payout.createdAt)}</span>
            <span>
              {receiptsExperience ? 'Previsão de chegada' : 'Previsão de chegada (UTC)'}:{' '}
              {arrivalDate(payout.arrivalDate)}
            </span>
            <Button variant="secondary" onClick={() => setSelectedPayout(payout.id)}>
              Ver repasse {payout.id}
            </Button>
          </div>
        ))}
        {!payouts.isPending && !payouts.isError && transfers.length === 0 ? (
          <EmptyState
            icon={<WalletCards size={22} />}
            title={
              payouts.data?.pages[0]?.syncStatus === 'current'
                ? 'Nenhum repasse'
                : 'Repasses ainda não disponíveis'
            }
            description="Os repasses observados aparecerão aqui após a sincronização."
          />
        ) : null}
        <LoadMore
          shown={transfers.length}
          hasMore={Boolean(payouts.hasNextPage)}
          loading={payouts.isFetchingNextPage}
          onClick={() => {
            void payouts.fetchNextPage()
          }}
        />
      </Card>
      <PayoutDetail
        key={selectedPayout ?? 'closed'}
        id={selectedPayout}
        onClose={() => setSelectedPayout(null)}
        onViewSale={
          onViewSale
            ? (id) => {
                setSelectedPayout(null)
                onViewSale(id)
              }
            : undefined
        }
      />
    </div>
  )
}
